import { useEffect, useState } from "react"

const CLOCK_TICK_MS = 60 * 1000

/** `setTimeout` stores its delay in a signed 32-bit int; anything larger wraps and fires at once. */
const MAX_TIMEOUT_MS = 2 ** 31 - 1

/**
 * WHETHER THE READING KEEPS MOVING BETWEEN CROSSINGS, and an argument rather than a default because
 * the wrong answer is invisible: both render identically and only one of them costs a render a
 * minute, forever, to every member on the surface.
 *
 * `"minutely"` for a surface that DRAWS the time. `@internal/telemedicine`'s rail counts a visit
 * down ("Starting in 9 minutes", "in 3 days") and reads "Today" off `now`, so every minute the
 * reading does not move is a minute of copy that is wrong by a minute.
 *
 * `"at-the-crossing-only"` for a surface whose ONLY clock-dependent output is which side of
 * `crossesAt` it is on. `@internal/consumer`'s plan card is one: its visit copy is ABSOLUTE
 * (`Jun 12`, `11:00 AM PT`) and the single thing it derives from the clock is the imminence
 * boolean, which the one-shot below lands exactly. An interval on top of that changes nothing a
 * member can see and re-renders the whole plan — every card of the stack — once a minute for EVERY
 * member, including the majority whose `crossesAt` is `null` because they have no visit booked at
 * all. That unconditional interval is what the CONAPP-343 review found; this parameter is the fix.
 */
type ClockTicks = "minutely" | "at-the-crossing-only"

/**
 * One `now` for a surface whose copy changes as a moment approaches — advanced by a one-shot AT that
 * moment, and by a tick a minute as well for a caller that asks for one.
 *
 * ⚠ AN INTERVAL ALONE IS NOT ENOUGH, and that is the whole reason this is a hook rather than a bare
 * `setInterval`. Its boundaries align to *mount*, not to the thing being waited on, so a card
 * mounted 30m10s out from an appointment would cross the imminence threshold ten seconds later but
 * not re-render for another fifty — stranding the member on `Complete forms` at the exact moment
 * `Join` should appear. Hence the one-shot timeout AT the crossing.
 *
 * ⚠ AN INTERVAL IS NOT ALWAYS WANTED, EITHER, which is what `ticks` answers — see `ClockTicks`, and
 * do not give it a default. Every caller states which it needs.
 *
 * ⚠ HOISTED HERE FROM `@internal/telemedicine`'s `useAppointmentTiming` by CONAPP-353, which needed
 * the identical clock on `@internal/consumer`'s plan card — a package that cannot import the
 * telemedicine one. Copying it would have reintroduced the trap above in the copy, quietly, since a
 * bare interval passes every test that does not sit on a boundary.
 *
 * `crossesAt` is the instant to land a render on, or `null` when there is none — pass
 * `getImminentThresholdInstant(date)` from `@internal/constants/telemedicine/imminence` to line the
 * timeout up with the predicate exactly. The timeout is skipped when the crossing is already behind
 * us, when there is no date at all (`NaN` fails both comparisons) and when it is far enough out to
 * overflow the timer; in all three there is nothing about to flip.
 *
 * WHAT THIS DOES NOT DO is derive anything. It answers "what time is it, re-checked", and the caller
 * decides what that means — which keeps the derivation itself a pure function of `(data, now)` and
 * testable without fake timers.
 */
export function useNow(crossesAt: Date | null, ticks: ClockTicks): Date {
  const [now, setNow] = useState(() => new Date())
  // A primitive dep, so a fresh `Date` for the same instant doesn't reset the timers.
  const crossesAtMs = crossesAt?.getTime()

  useEffect(() => {
    const tick = ticks === "minutely" ? setInterval(() => setNow(new Date()), CLOCK_TICK_MS) : null
    const delayMs = (crossesAtMs ?? NaN) - Date.now()
    const crossing = delayMs > 0 && delayMs <= MAX_TIMEOUT_MS ? setTimeout(() => setNow(new Date()), delayMs) : null

    // ⚠ A CROSSING HANDED OVER ALREADY BEHIND US GETS NO TIMER, and with no interval there would be
    // nothing left to refresh a reading taken before it — so that one case is closed here. It is
    // what a REFETCH looks like on a long-open page: a visit that appears, or moves, already inside
    // its window. A reading older than the crossing would answer the wrong side of it with no timer
    // pending to correct itself, which is the same stranded member the one-shot exists to prevent.
    // The functional update makes this idempotent — it re-reads only while the reading predates the
    // crossing — so it cannot loop even a caller that re-derives `crossesAt` from `now`.
    // `"minutely"` needs none of it: its own tick already bounds staleness at sixty seconds.
    if (ticks === "at-the-crossing-only" && crossesAtMs !== undefined && delayMs <= 0) {
      setNow((current) => (current.getTime() < crossesAtMs ? new Date() : current))
    }

    return () => {
      clearInterval(tick ?? undefined)
      clearTimeout(crossing ?? undefined)
    }
  }, [crossesAtMs, ticks])

  return now
}
